import { z } from 'zod';

// ─── Changelog Event Payloads ───────────────────────────────────────
//
// Shape of data carried on Pub/Sub messages for admin changelog generation.
// Pair each schema with a name from `SystemEvents` in
// `constants/event-names.ts`.
//
// Who uses this:
//   - services/api   → reads GitHub, persists the entry, publishes the request
//   - services/users → validates in the handler via `withErrorHandling(schema, …)`
//                      and turns the commit list into user-facing notes
//
// The API is the only side that talks to GitHub, so the commit list travels on
// the wire rather than being re-fetched by the worker — that keeps the GitHub
// token in one service.

/** One merged change, already deduplicated by PR/ticket on the API side. */
export const ChangelogCommitSchema = z
  .object({
    /** Commit subject, or the PR title when the merge commit named one. */
    subject: z.string(),
    shortSha: z.string(),
    /** Jira key referenced by the commit, e.g. `AD-2075`. */
    ticket: z.string().nullable(),
  })
  .strict();
export type ChangelogCommit = z.infer<typeof ChangelogCommitSchema>;

/** Sections follow the design's user-facing grouping, not the Slack grouping. */
export const CHANGELOG_SECTIONS = ['new', 'improved', 'fixed'] as const;
export const ChangelogSectionKeySchema = z.enum(CHANGELOG_SECTIONS);
export type ChangelogSectionKey = z.infer<typeof ChangelogSectionKeySchema>;

/**
 * One rendered bullet. `topic` is the short human label the reader recognises
 * at a glance ("Opportunities", "Workspace header"); `ticket` is rendered as a
 * Jira link by the client rather than being baked into `description`.
 */
export const ChangelogItemSchema = z
  .object({
    topic: z.string().min(1),
    description: z.string().min(1),
    ticket: z.string().nullable(),
  })
  .strict();
export type ChangelogItem = z.infer<typeof ChangelogItemSchema>;

/** The generated notes for one deploy, keyed by section. */
export const ChangelogSectionsSchema = z
  .object({
    new: z.array(ChangelogItemSchema),
    improved: z.array(ChangelogItemSchema),
    fixed: z.array(ChangelogItemSchema),
  })
  .strict();
export type ChangelogSections = z.infer<typeof ChangelogSectionsSchema>;

// ── CHANGELOG_GENERATE_REQUESTED ─────────────────────────────────────
// Event: SystemEvents.CHANGELOG_GENERATE_REQUESTED
// When:  a production deploy is detected without notes, or an admin asks for
//        a regenerate.

export const ChangelogGenerateRequestedPayload = z
  .object({
    versionTag: z.string().min(1),
    versionSha: z.string().min(1),
    previousVersionTag: z.string().nullable(),
    commits: z.array(ChangelogCommitSchema),
  })
  .strict();
export type ChangelogGenerateRequested = z.infer<typeof ChangelogGenerateRequestedPayload>;

// ── CHANGELOG_DELIVER_REQUESTED ──────────────────────────────────────
// Event: SystemEvents.CHANGELOG_DELIVER_REQUESTED
// When:  an admin sends approved notes to one or more channels.
//
// Only the tag and the channels travel: the notes themselves are already in the
// database, and re-reading them in the worker means a redelivery can never send
// a stale copy of text that was corrected in between.

export const ChangelogChannelSchema = z.enum(['IN_APP', 'SLACK', 'EMAIL']);
export type ChangelogChannel = z.infer<typeof ChangelogChannelSchema>;

export const ChangelogDeliverRequestedPayload = z
  .object({
    versionTag: z.string().min(1),
    entryId: z.string().min(1),
    channels: z.array(ChangelogChannelSchema).min(1),
  })
  .strict();
export type ChangelogDeliverRequested = z.infer<typeof ChangelogDeliverRequestedPayload>;

// ── CHANGELOG_DIGEST_GENERATE_REQUESTED ──────────────────────────────
// Event: SystemEvents.CHANGELOG_DIGEST_GENERATE_REQUESTED
// When:  an admin composes a digest, or regenerates one.
//
// The composer's selection travels on the wire rather than being persisted:
// what is worth keeping is the merged result, and re-reading a stale selection
// on a redelivery would produce notes that no longer match what was ticked.

/** One entry item offered to the merge, with the deploy it came from. */
export const ChangelogDigestSourceSchema = z
  .object({
    itemId: z.string().min(1),
    versionTag: z.string().min(1),
    section: ChangelogSectionKeySchema,
    topic: z.string().min(1),
    description: z.string().min(1),
    ticket: z.string().nullable(),
  })
  .strict();
export type ChangelogDigestSource = z.infer<typeof ChangelogDigestSourceSchema>;

export const ChangelogDigestGenerateRequestedPayload = z
  .object({
    digestId: z.string().min(1),
    title: z.string().min(1),
    sources: z.array(ChangelogDigestSourceSchema),
  })
  .strict();
export type ChangelogDigestGenerateRequested = z.infer<
  typeof ChangelogDigestGenerateRequestedPayload
>;

// ── CHANGELOG_DIGEST_DELIVER_REQUESTED ───────────────────────────────
// Event: SystemEvents.CHANGELOG_DIGEST_DELIVER_REQUESTED
// When:  an admin sends an approved digest to email.
//
// In-app is broadcast by the API inline, as it is for an entry, so only the
// channels a worker actually owns travel here.

export const ChangelogDigestDeliverRequestedPayload = z
  .object({
    digestId: z.string().min(1),
    channels: z.array(ChangelogChannelSchema).min(1),
    /** Same origin the API used for the send-dialog preview (`API_APP_BASE_URL`). */
    appBaseUrl: z.string().optional(),
  })
  .strict();
export type ChangelogDigestDeliverRequested = z.infer<
  typeof ChangelogDigestDeliverRequestedPayload
>;

// ── CHANGELOG_SYNC_REQUESTED ─────────────────────────────────────────
// Event: SystemEvents.CHANGELOG_SYNC_REQUESTED
// When:  Cloud Scheduler (or an operator's Run now) asks the API to detect
//        new GitHub releases and flush held unattended posts.
//
// Empty on purpose: the detector reads GitHub and the database; nothing the
// caller could put on the envelope would change what it does.

export const ChangelogSyncRequestedPayload = z.object({}).strict();
export type ChangelogSyncRequested = z.infer<typeof ChangelogSyncRequestedPayload>;
